import type { DealerOptionItemDto } from '@api/machines'
import {
  type EqOptionType,
  groupBy,
  handleIdGeneration,
  Options,
  setIsPristine
} from '@common/utils'
import {
  MolsonBox,
  MolsonCustomTooltip,
  MolsonIcon,
  MolsonToggle,
  MolsonTypography
} from '@components/reusable'
import { molsonUseTheme } from '@theme/customMuiExports'
import { useCallback, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useParams } from 'react-router-dom'
import { CollapseItems } from '../collapse-items/CollapseItems'
import { CustomFit, type SaveFn } from '../custom-fit/CustomFit'
import { type CommonDealerFitProps, DealerFitOptions } from './DealerFitOptions'

type DealerFitsProps = {
  dealerFitsChecked: boolean
  dealerFitsOptions: Record<string, DealerOptionItemDto[]>
  expandedFamilies?: Set<string>
  updateDealerFitQuantity?: (props: {
    actionType: 'decrease' | 'increase'
    itemQuantity: number
    optionId: number
  }) => void
  toggleDealerFitChecked?: () => void
  addCustomDealerFit?: SaveFn
  setExpandedFamilies?: (value: string) => void
  closeExpandedFamilyOrGroup?: (
    eqOptionType: EqOptionType,
    familyOrGroup: string
  ) => void
}

export const DealerFits = ({
  dealerFitsChecked,
  dealerFitsOptions,
  expandedFamilies,
  handleOptionChange,
  handleUpdateDealerFitQuantity,
  updateDealerFitQuantity,
  quotationItem,
  quotationItemOldPrices,
  selectedDealerOptions,
  setExpandedFamilies,
  closeExpandedFamilyOrGroup,
  addCustomDealerFit,
  toggleDealerFitChecked,
  setSelectedDealerOptions
}: DealerFitsProps & CommonDealerFitProps) => {
  const [customDealerFitExpanded, setCustomDealerFitExpanded] = useState(false)

  const { t } = useTranslation('', {
    keyPrefix: 'common'
  })

  const { attachmentId, equipmentId, quoteId } = useParams()

  const isWithinMachine = Boolean(equipmentId && attachmentId)

  const theme = molsonUseTheme()

  const toggleLabel = dealerFitsChecked
    ? t('hideDealerfitOptions')
    : t('showDealerfitOptions')

  const onDeleteDealerFit = (id: number) => {
    handleOptionChange({
      machineId: isWithinMachine ? equipmentId : undefined,
      option: isWithinMachine
        ? Options.REMOVE_DEALER_FIT_WITHIN_MACHINE
        : Options.REMOVE_DEALER_FIT,
      optionId: id,
      quotationId: quoteId as string
    })
    setIsPristine(false)
  }

  const selectedCustomDealerOptions = quotationItem.dealerOptions ?? []
  const customDealerOptions = selectedCustomDealerOptions.filter(
    item => (item?.id ?? 0) < 0
  )

  const options =
    customDealerOptions.length > 0
      ? customDealerOptions.map(item => ({
          dnp: item.dnp,
          id: item.id,
          name: item.itemName,
          isNewValue: false
        }))
      : [
          {
            dnp: 0,
            id: Number(handleIdGeneration()),
            name: '',
            isNewValue: true
          }
        ]

  const hasArchivedDealerOptions = quotationItem.dealerOptions?.some(
    item => item.isArchived
  )

  const hideFamiliesWithNoOptions = useCallback(() => {
    const archivedFamilies = selectedDealerOptions.map(option => option.family)
    for (const family in dealerFitsOptions) {
      const allArchived = dealerFitsOptions[family].every(
        item => item.isArchived
      )

      if (allArchived && !archivedFamilies.includes(family)) {
        delete dealerFitsOptions[family]
      }
    }
  }, [dealerFitsOptions, selectedDealerOptions])

  useEffect(() => {
    hideFamiliesWithNoOptions()
  }, [hideFamiliesWithNoOptions])

  return (
    <MolsonBox
      sx={{
        background: theme.palette.secondary[50],
        borderRadius: '8px',
        px: 3,
        py: 5
      }}
      boxShadowType="default"
      id="archived-section"
      mt={2.5}
    >
      <MolsonBox
        alignItems="center"
        display="flex"
        justifyContent="space-between"
        mb={dealerFitsChecked ? 4 : 0}
      >
        <MolsonBox alignItems="center" display="flex" gap={0.5}>
          {hasArchivedDealerOptions && (
            <MolsonIcon variant="ERROR_OUTLINE" iconColor="error" />
          )}
          <MolsonTypography variant="h3">{t('dealerFit')}</MolsonTypography>
          <MolsonCustomTooltip
            config={{
              description: t('dealerfitOptionsAmendments'),
              title: t('dealerFit')
            }}
          />
        </MolsonBox>
        <MolsonToggle
          onChange={() =>
            // setDealerFitsChecked((prevValue: boolean) => !prevValue)
            toggleDealerFitChecked?.()
          }
          checked={dealerFitsChecked}
          label={toggleLabel}
        />
      </MolsonBox>

      <CollapseItems checked={dealerFitsChecked}>
        {dealerFitsOptions &&
          Object.entries(dealerFitsOptions).map(
            ([familyName, familyOptions]) => (
              <DealerFitOptions
                expandedFamilies={expandedFamilies}
                handleOptionChange={handleOptionChange}
                handleUpdateDealerFitQuantity={handleUpdateDealerFitQuantity}
                updateDealerFitQuantity={updateDealerFitQuantity}
                key={familyName}
                name={familyName}
                options={groupBy(familyOptions, 'brand')}
                quotationItem={quotationItem}
                quotationItemOldPrices={quotationItemOldPrices}
                selectedDealerOptions={selectedDealerOptions}
                setExpandedFamilies={setExpandedFamilies}
                setSelectedDealerOptions={setSelectedDealerOptions}
                closeExpandedFamilyOrGroup={closeExpandedFamilyOrGroup}
                ungroupedOptions={familyOptions}
              />
            )
          )}

        <CustomFit
          expanded={customDealerFitExpanded}
          key={selectedCustomDealerOptions.length}
          onDelete={onDeleteDealerFit}
          onSave={addCustomDealerFit}
          options={options}
          setExpanded={setCustomDealerFitExpanded}
        />
      </CollapseItems>
    </MolsonBox>
  )
}
